import { Component, OnDestroy, OnInit } from '@angular/core'
import { FormGroup, FormBuilder } from '@angular/forms'
import { ActivatedRoute } from '@angular/router'
import { GlobalConstants } from 'src/app/constants/global-constants'
import { ServiceUrlConstants } from 'src/app/constants/service-url-constants'
import { CommentAPIModel, QueriesAPIModel } from 'src/app/models/api.model'
import { QueryService } from 'src/app/services/query/query.service'
import { CommonService, formatDateToDDMMYYYY } from 'src/app/services/common/common.service'
import { DocumentService } from 'src/app/services/common/document.service'
import { Subscription } from 'rxjs'
import { InitializeService } from '../../../services/initialize/initialize.service'

@Component({
  selector: 'app-query-details',
  templateUrl: './query-details.component.html',
  styleUrls: ['./query-details.component.scss']
})
export class QueryDetailsComponent implements OnInit, OnDestroy {
  queryDetailsForm: FormGroup
  query: QueriesAPIModel
  commentsToSave: CommentAPIModel[] = []
  private id: string
  readonly QUERY_DASHBOARD_LINK = `/${GlobalConstants.ROUTING.QUERY}/${GlobalConstants.ROUTING.DASHBOARD}`
  filesToBeUploaded: File[]
  hasFormChanged: boolean
  notify: boolean
  isQueryUnlocked: boolean = false
  queryDetailsSubscription: Subscription
  selectStatusListFromState$: Subscription
  readonly CREATED_DATETIME_CONTROL_NAME = 'createdDatetime'
  readonly EPFO_TIMELINE_CONTROL_NAME = 'epfoTimeline'
  readonly RESPONSE_DUE_DATETIME_CONTROL_NAME = 'responseDueDatetime'
  statusLabel: string

  constructor(
    private queryService: QueryService,
    private route: ActivatedRoute,
    private formBuilder: FormBuilder,
    private cs: CommonService,
    private documentService: DocumentService,
    private initializeService: InitializeService
  ) {
  }

  ngOnInit() {
    this.buildQueryDetailsForm()
    this.route.params.subscribe((params) => {
      const id = params['id']
      this.id = id
      this.dispatchFetchQueryDetailsAction(id)
      this.getQueryDetailsFromStore()
    })
  }

  private buildQueryDetailsForm() {
    this.queryDetailsForm = this.formBuilder.group({
      displayId: [{ value: '', disabled: true }],
      title: [{ value: '', disabled: true }],
      type: [{ value: '', disabled: true }],
      createdDatetime: [{ value: '', disabled: true }],
      status: [{ value: '', disabled: true }, []],
      detailedStatus: ['', []],
      description: [{ value: '', disabled: true }, []],
      statusUpdateFlag: [false, []],
      nextAction: [{ value: '', disabled: true }, []],
      epfoTimeline: [{ value: '', disabled: true }, []],
      responseDueDatetime: [{ value: '', disabled: true }]
    })
  }

  get f() {
    return this.queryDetailsForm.controls
  }

  private dispatchFetchQueryDetailsAction(id: any) {
    this.queryService.dispatchFetchQueryDetailsAction(id)
  }

  private getQueryDetailsFromStore() {
    this.queryDetailsSubscription = this.queryService.getQueryDetailsFromStore().subscribe((query) => {
      if (query) {
        this.query = query
        if (this.query.status.toLocaleLowerCase() !== 'completed') {
          this.isQueryUnlocked = true
        }
        this.queryDetailsForm.patchValue(query)
        this.queryDetailsForm.controls[this.CREATED_DATETIME_CONTROL_NAME].setValue(formatDateToDDMMYYYY(query.createdDatetime))
        this.queryDetailsForm.controls[this.EPFO_TIMELINE_CONTROL_NAME].setValue(formatDateToDDMMYYYY(query?.epfoTimeline))
        this.queryDetailsForm.controls[this.RESPONSE_DUE_DATETIME_CONTROL_NAME].setValue(formatDateToDDMMYYYY(query?.responseDueDatetime))
        this.subscribeFormForValueChanges(this.queryDetailsForm)

        this.selectStatusListFromState$ = this.initializeService
          .getStatusListByCodeFromState(this.query.taskCategory, this.query.taskSubCategory, GlobalConstants.MODULE.QUERIES, this.query.taskCode)
          .subscribe((statusList) => {
            if (statusList) {
              const status = statusList.find(item => item.code === this.query?.status)
              this.statusLabel = status ? status.label : ''
            }
          })
      }
    })
  }

  addComment(commentAPIModel: CommentAPIModel) {
    this.commentsToSave = [commentAPIModel]
  }

  getIfNotifySelected(notify) {
    this.notify = notify || false
  }

  updateQuery() {
    if (this.queryDetailsForm.invalid) {
      this.queryDetailsForm.markAllAsTouched()
      return
    }

    if (this.commentsToSave?.length === 0 && this.filesToBeUploaded?.length === 0) {
      return this.cs.route(this.QUERY_DASHBOARD_LINK)
    }

    const commentToBeSaved = this.commentsToSave && this.commentsToSave[0]?.comment ? this.commentsToSave : []
    const notifyState = this.determineNotifyState(commentToBeSaved) || false

    this.queryService.dispatchUpdateQueryAction(
      {
        ...this.queryDetailsForm.value,
        title: this.query.title,
        statusUpdateFlag: false,
        comments: commentToBeSaved,
        notify: notifyState,
        nextInternalDueDate: this.query?.nextInternalDueDate || null,
        nextAction: this.query?.nextAction || null,
        epfoTimeline: this.query?.epfoTimeline || null
      },
      this.id,
      this.filesToBeUploaded,
      this.QUERY_DASHBOARD_LINK
    )
  }

  downloadQuery() {
    const endPoint = ServiceUrlConstants.createDownloadExportPDFEndpoint('QUERIES', this.id)
    this.documentService.downloadDocument(endPoint)
  }

  handleFileInput(files: File[]) {
    this.filesToBeUploaded = files
  }

  clearSelectedFiles(flag: boolean) {
    if (flag) {
      this.filesToBeUploaded = null
    }
  }

  private subscribeFormForValueChanges(formGroup: FormGroup) {
    const initialValue = formGroup.value
    formGroup.valueChanges.subscribe((value) => {
      this.hasFormChanged = Object.keys(initialValue).some(
        (key) => formGroup.value[key] != initialValue[key] && formGroup.dirty
      )
    })
  }

  determineNotifyState(commentToBeSaved: CommentAPIModel[]) {
    const hasCommented = commentToBeSaved?.length > 0
    const hasUploadedFiles = this.filesToBeUploaded !== null && this.filesToBeUploaded?.length > 0
    return ((this.hasFormChanged != null && this.hasFormChanged) || hasCommented || hasUploadedFiles) && this.notify
  }

  ngOnDestroy(): void {
    this.selectStatusListFromState$?.unsubscribe()
    this.queryDetailsSubscription?.unsubscribe()
    this.queryService.dispatchDeleteQueryDetailsAction()
  }
}
